// src/top50/shared.js — utilities shared between admin and staff sides
//
// Path-to-8 Stage 5 / Phase 2a (May 6 2026): pure helper functions that
// were duplicated across admin.html and index.html. Extracting them here
// removes the duplication and gives the bundle its first real exports.
//
// All functions in this file are PURE — no DOM access, no DB calls, no
// external state. They can be unit-tested in isolation.
//
// Phase 2 sequence:
//   2a (this commit): shared utilities only
//   2b: admin.js — upload, parse, preview, commit, history, delete
//   2c: staff.js — view, render, selectors, AI review, store rank
//   Phase 3: HTML cutover — remove inline copies, wire Top50.* calls

// HTML escape helpers. Top 50 has its own copies (rather than using
// the codebase's `escapeHtml`) so the bundle is self-contained — no
// runtime dependency on inline HTML globals for these basic utilities.
export function escHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function escAttr(s) {
  return escHtml(s);
}

// Render compact UAE / Qatar product-class badges if a class is noteworthy.
// Numeric classes (100, 101, etc.) are normal active stock — no badge.
// PROMO* = orange "Promo" badge. CONSO = grey "DISCONTINUED" badge.
// OOS / "not created" → red small label.
export function top50ClassBadges(uae, qatar) {
  function badge(label, country) {
    var c = String(label || '').trim();
    if (!c) return '';
    if (/^PROMO/i.test(c)) {
      return '<span style="display:inline-block;background:var(--warn-bg);border:1px solid var(--stores-amber);color:var(--stores-amber);padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;margin-inline-end:3px" title="Promo line (' + country + ')">PROMO ' + country + '</span>';
    }
    if (c === 'CONSO') {
      return '<span style="display:inline-block;background:var(--surface-3);border:1px solid var(--stores-slate);color:var(--stores-slate);padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;margin-inline-end:3px" title="Discontinued — do not reorder (' + country + ')">DISCONTINUED ' + country + '</span>';
    }
    if (c === 'OOS' || /^not\s*created$/i.test(c)) {
      return '<span style="display:inline-block;background:var(--danger-bg);border:1px solid var(--stores-down);color:var(--stores-down);padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;margin-inline-end:3px" title="' + (c === 'OOS' ? 'Out of stock at warehouse' : 'Not created in this market') + ' (' + country + ')">' + escHtml(c) + ' ' + country + '</span>';
    }
    // Numeric / normal active class — no badge
    return '';
  }
  return badge(uae, 'UAE') + badge(qatar, 'QAT');
}

// v80.4 — normalise store names for case-insensitive prefix-tolerant
// comparison. store_data keys come from the spreadsheet
// ("ELC - DUBAI - DUBAI HILLS") while currentStore comes from the
// staff/stores table ("Dubai - Dubai Hills"). They refer to the same
// place but won't compare equal as strings. This strips the "ELC -"
// prefix, lowercases, and collapses whitespace so the two forms match.
//
// v85.0 — retained as the FALLBACK for stores without an explicit
// upload_name alias. The preferred path is findTop50StoreKey below,
// which consults stores.upload_name first.
export function normTop50StoreName(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .replace(/^\s*elc\s*-\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// v85.0 — build a fast canonicalName → upload_name lookup from the
// ALL_STORES array (or any list of {name, upload_name} objects loaded
// from /api/stores). The map's value is the raw upload_name string,
// or null when the store has no alias set.
//
// Call once per render (cheap — 28 stores), pass the result into
// findTop50StoreKey for each entry.
export function buildTop50AliasMap(stores) {
  var map = Object.create(null);
  if (!Array.isArray(stores)) return map;
  for (var i = 0; i < stores.length; i++) {
    var s = stores[i];
    if (s && s.name) {
      map[s.name] = (s.upload_name && String(s.upload_name).trim()) || null;
    }
  }
  return map;
}

// v85.0 — find the matching store_data key for a canonical store name.
// This is what Top 50 should have used from day one — daily/budget/addon
// uploads already use stores.upload_name via buildUploadNameMap() in
// admin.html, but Top 50 was implemented later and missed the pattern,
// hence the Arabian Ranches 2 / Marina Mall / Landmark bugs.
//
// Parameters:
//   canonicalName  - the rota-system store name (e.g. "Dubai - Arabian Ranches 2")
//   aliasMap       - result of buildTop50AliasMap(ALL_STORES)
//   availableKeys  - Object.keys(entry.store_data) for the entry being checked
//
// Lookup precedence:
//   1. Exact match on aliasMap[canonicalName] (explicit alias — preferred)
//   2. Case-insensitive match on the alias (defensive — admin typed wrong case)
//   3. Normalised match on canonicalName itself (legacy / unaliased stores)
//   4. null (no match — caller should treat as "store not in this Top 50 file")
//
// Returns the matching key string from availableKeys so the caller can do
// entry.store_data[returnedKey] directly. Never returns the canonicalName
// itself unless it happens to equal a key in availableKeys.
export function findTop50StoreKey(canonicalName, aliasMap, availableKeys) {
  if (!canonicalName || !Array.isArray(availableKeys) || availableKeys.length === 0) {
    return null;
  }
  // Path 1 & 2: explicit alias (preferred)
  var alias = aliasMap && aliasMap[canonicalName];
  if (alias) {
    if (availableKeys.indexOf(alias) !== -1) return alias;
    var aliasLower = String(alias).toLowerCase();
    for (var i = 0; i < availableKeys.length; i++) {
      if (String(availableKeys[i]).toLowerCase() === aliasLower) {
        return availableKeys[i];
      }
    }
  }
  // Path 3: normalised fallback for stores without an alias set yet
  var canonicalNorm = normTop50StoreName(canonicalName);
  for (var j = 0; j < availableKeys.length; j++) {
    if (normTop50StoreName(availableKeys[j]) === canonicalNorm) {
      return availableKeys[j];
    }
  }
  return null;
}

// YYYY-MM-DD → "DD MMM YYYY" (compact, locale-independent).
// Used in both admin (history list) and staff (week selector).
export function formatTop50Date(iso) {
  if (!iso) return '';
  var p = iso.split('-');
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var mIdx = parseInt(p[1], 10) - 1;
  return p[2] + ' ' + (months[mIdx] || p[1]) + ' ' + p[0];
}

// YYYY-MM-DD → "DD MMM" (no year). Used in staff week selector for
// compact week-range display: "5 May → 11 May".
export function formatTop50DateShort(iso) {
  if (!iso) return '';
  var p = iso.split('-');
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return p[2] + ' ' + (months[parseInt(p[1],10)-1] || p[1]);
}
